import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Upload, FileText, ClipboardPaste, Download, CheckCircle2,
  AlertTriangle, ChevronRight, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'file',     label: 'Upload File',    icon: Upload },
  { id: 'paste',    label: 'Paste Rows',     icon: ClipboardPaste },
  { id: 'template', label: 'Template',       icon: FileText },
];

const COLUMN_ORDER = [
  'load_type', 'pickup', 'destination', 'weight', 'truck_type',
  'commodity', 'pickup_date', 'delivery_date', 'rate', 'miles', 'latest_date',
];

export const BulkLoadUploadModal = ({ onClose, onSuccess }) => {
  const [tab,       setTab]       = useState('file');
  const [parsing,   setParsing]   = useState(false);
  const [rows,      setRows]      = useState(null); // [{row_num, data, errors}]
  const [committing,setCommitting]= useState(false);
  const [pasteText, setPasteText] = useState('');
  const [dragOver,  setDragOver]  = useState(false);
  const fileInputRef = useRef(null);

  const parseFile = useCallback(async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File is too large (max 8 MB)');
      return;
    }
    setParsing(true);
    setRows(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/loads/bulk/parse`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRows(res.data.rows);
      toast.success(`Parsed ${res.data.total} rows — ${res.data.valid} valid, ${res.data.invalid} need attention`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse file');
    } finally {
      setParsing(false);
    }
  }, []);

  const parsePaste = useCallback(async () => {
    if (!pasteText.trim()) {
      toast.error('Paste some rows first');
      return;
    }
    setParsing(true);
    setRows(null);
    try {
      const fd = new FormData();
      fd.append('paste_text', pasteText);
      const res = await axios.post(`${API}/loads/bulk/parse`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRows(res.data.rows);
      toast.success(`Parsed ${res.data.total} rows — ${res.data.valid} valid, ${res.data.invalid} need attention`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse');
    } finally {
      setParsing(false);
    }
  }, [pasteText]);

  const commit = useCallback(async () => {
    const validLoads = (rows || []).filter((r) => !r.errors.length).map((r) => r.data);
    if (!validLoads.length) {
      toast.error('No valid rows to post');
      return;
    }
    setCommitting(true);
    try {
      const res = await axios.post(`${API}/loads/bulk/commit`, { loads: validLoads });
      toast.success(`Posted ${res.data.created} load${res.data.created !== 1 ? 's' : ''}`);
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post loads');
    } finally {
      setCommitting(false);
    }
  }, [rows, onSuccess]);

  const validCount   = rows ? rows.filter((r) => !r.errors.length).length : 0;
  const invalidCount = rows ? rows.filter((r) =>  r.errors.length).length : 0;

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) parseFile(f);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="bulk-upload-modal"
    >
      <div
        className="bg-white w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <h2 className="text-lg font-black text-zinc-900 uppercase tracking-wider">Bulk Upload Loads</h2>
            <p className="text-xs text-zinc-500 mt-0.5">CSV, XLSX, or paste from your spreadsheet · up to 500 rows</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 text-2xl leading-none" data-testid="bulk-close-btn">×</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-zinc-200 px-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-colors ${
                tab === t.id ? 'border-red-600 text-red-600' : 'border-transparent text-zinc-500 hover:text-zinc-900'
              }`}
              data-testid={`bulk-tab-${t.id}`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'file' && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed p-12 text-center transition-colors ${
                dragOver ? 'border-red-500 bg-red-50' : 'border-zinc-300 bg-zinc-50'
              }`}
              data-testid="bulk-file-dropzone"
            >
              <Upload className="w-10 h-10 mx-auto mb-3 text-zinc-400" />
              <div className="text-sm font-bold text-zinc-900 mb-1">Drop your CSV or XLSX here</div>
              <div className="text-xs text-zinc-500 mb-4">or</div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={parsing}
                className="bg-zinc-900 hover:bg-red-600 text-white px-5 py-2 text-xs font-black uppercase tracking-wider transition-colors disabled:opacity-50"
                data-testid="bulk-choose-file-btn"
              >
                {parsing ? 'Parsing…' : 'Choose file'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xlsm"
                className="hidden"
                onChange={(e) => parseFile(e.target.files?.[0])}
                data-testid="bulk-file-input"
              />
              <div className="text-[11px] text-zinc-400 mt-6">
                Supported columns: {COLUMN_ORDER.join(', ')}
              </div>
            </div>
          )}

          {tab === 'paste' && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                Paste rows (CSV or tab-separated from Excel/Google Sheets)
              </div>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={`load_type,pickup,destination,weight,truck_type,commodity,pickup_date,delivery_date\nFTL,Chicago IL,Dallas TX,40000,Dry Van,Steel,2026-07-15,2026-07-18\nFTL,Houston TX,Miami FL,38000,Reefer,Produce,2026-07-16,2026-07-20`}
                rows={10}
                className="w-full font-mono text-xs px-3 py-2 border border-zinc-300 focus:border-zinc-900 outline-none"
                data-testid="bulk-paste-textarea"
              />
              <div className="flex justify-between items-center mt-3">
                <span className="text-[11px] text-zinc-500">Include a header row with column names.</span>
                <button
                  onClick={parsePaste}
                  disabled={parsing || !pasteText.trim()}
                  className="bg-zinc-900 hover:bg-red-600 text-white px-5 py-2 text-xs font-black uppercase tracking-wider transition-colors disabled:opacity-50"
                  data-testid="bulk-paste-parse-btn"
                >
                  {parsing ? 'Parsing…' : 'Parse rows'}
                </button>
              </div>
            </div>
          )}

          {tab === 'template' && (
            <div className="bg-zinc-50 border border-zinc-200 p-6">
              <h3 className="font-black text-sm uppercase tracking-wider text-zinc-900 mb-2">CSV Template</h3>
              <p className="text-xs text-zinc-600 mb-4 leading-relaxed">
                Download the template, fill in your loads (one per row), then upload it back via the <strong>Upload File</strong> tab.
                Column order is flexible — we match by header name. Required columns:{' '}
                <code className="bg-white px-1 py-0.5 text-[11px]">load_type, pickup, destination, weight, truck_type, commodity, pickup_date, delivery_date</code>.
              </p>
              <a
                href={`${API}/loads/bulk/template`}
                download="load-upload-template.csv"
                className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-red-600 text-white px-5 py-2.5 text-xs font-black uppercase tracking-wider transition-colors"
                data-testid="bulk-download-template-btn"
              >
                <Download className="w-3.5 h-3.5" />
                Download CSV Template
              </a>

              <div className="mt-6 text-xs">
                <div className="font-black uppercase tracking-wider text-zinc-500 mb-2">Column reference</div>
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[10px] uppercase text-zinc-400">
                      <th className="pb-1 pr-4">Column</th>
                      <th className="pb-1 pr-4">Required</th>
                      <th className="pb-1">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ColRow name="load_type"      req notes="FTL, LTL, Intermodal, Drayage" />
                    <ColRow name="pickup"         req notes="Origin (e.g. 'Chicago, IL')" />
                    <ColRow name="destination"    req notes="Delivery (e.g. 'Dallas, TX')" />
                    <ColRow name="weight"         req notes="Pounds (numeric)" />
                    <ColRow name="truck_type"     req notes="Dry Van, Flatbed, Reefer, Container, Power Only, Step Deck" />
                    <ColRow name="commodity"      req notes="Cargo description" />
                    <ColRow name="pickup_date"    req notes="YYYY-MM-DD" />
                    <ColRow name="delivery_date"  req notes="YYYY-MM-DD" />
                    <ColRow name="rate"               notes="USD (numeric, optional)" />
                    <ColRow name="miles"              notes="Integer (optional)" />
                    <ColRow name="latest_date"        notes="YYYY-MM-DD, latest pickup (optional)" />
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {rows && rows.length > 0 && (
            <div className="mt-6" data-testid="bulk-preview">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-black uppercase tracking-wider">
                  Preview · <span className="text-green-700">{validCount} valid</span>
                  {' · '}
                  {invalidCount > 0 && <span className="text-amber-700">{invalidCount} with errors</span>}
                  {invalidCount === 0 && <span className="text-zinc-400">0 with errors</span>}
                </div>
                <button
                  onClick={commit}
                  disabled={committing || validCount === 0}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 text-xs font-black uppercase tracking-wider transition-colors disabled:opacity-50"
                  data-testid="bulk-commit-btn"
                >
                  {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  {committing ? 'Posting…' : `Post ${validCount} load${validCount !== 1 ? 's' : ''}`}
                </button>
              </div>

              <div className="border border-zinc-200 overflow-hidden">
                <div className="overflow-x-auto max-h-[45vh]">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-100 sticky top-0">
                      <tr className="text-left">
                        <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-zinc-500 w-10">#</th>
                        {COLUMN_ORDER.map((c) => (
                          <th key={c} className="px-2 py-2 text-[10px] uppercase tracking-wider text-zinc-500">{c}</th>
                        ))}
                        <th className="px-2 py-2 text-[10px] uppercase tracking-wider text-zinc-500 w-40">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr
                          key={r.row_num}
                          className={r.errors.length ? 'bg-amber-50' : ''}
                          data-testid={`bulk-row-${r.row_num}`}
                        >
                          <td className="px-2 py-2 text-zinc-400">{r.row_num}</td>
                          {COLUMN_ORDER.map((c) => (
                            <td key={c} className="px-2 py-2 text-zinc-700 truncate max-w-[140px]">
                              {String(r.data[c] ?? '')}
                            </td>
                          ))}
                          <td className="px-2 py-2">
                            {r.errors.length ? (
                              <span title={r.errors.join('; ')} className="inline-flex items-center gap-1 text-amber-700 text-[11px] font-bold">
                                <AlertTriangle className="w-3 h-3" />
                                {r.errors.length} issue{r.errors.length > 1 ? 's' : ''}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-green-700 text-[11px] font-bold">
                                <CheckCircle2 className="w-3 h-3" />
                                Ready
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {invalidCount > 0 && (
                <div className="mt-3 bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-800">
                  <strong>Note:</strong> Rows with errors will be skipped. Fix your source file and re-parse, or click{' '}
                  <em>Post {validCount} load{validCount !== 1 ? 's' : ''}</em> to submit only the valid rows.
                </div>
              )}
            </div>
          )}

          {rows && rows.length === 0 && (
            <div className="mt-6 text-center py-10 text-xs text-zinc-500" data-testid="bulk-preview-empty">
              No rows found in your input.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ColRow = ({ name, req, notes }) => (
  <tr className="border-t border-zinc-200">
    <td className="py-2 pr-4 font-mono">{name}</td>
    <td className="py-2 pr-4">
      {req ? (
        <span className="text-red-600 font-bold">Required</span>
      ) : (
        <span className="text-zinc-400">Optional</span>
      )}
    </td>
    <td className="py-2 text-zinc-600">{notes}</td>
  </tr>
);
